/**
 * silentTensionDetector.js
 *
 * Detects IMPLICIT, UNSPOKEN pastoral tension.
 * NOT explicit conflict (conflictSensitivityEngine handles that).
 * NOT ethical danger (pastoralEthicsLayer handles that).
 *
 * This detects the gap between "everything's fine" and what the signals suggest —
 * evasion, minimization, inconsistency between stated state and emotional tone.
 *
 * When silent tension is detected, Kevia should:
 * — listen more than speak
 * — ask soft, open questions
 * — not push for resolution
 * — not name the tension directly unless invited
 * — create space for what hasn't been said yet
 *
 * Types:
 *   evasion                 — actively avoiding the real issue
 *   tension_espiritual      — spiritual unease not named
 *   conflicto_indirecto     — conflict hinted at but not stated
 *   agotamiento_oculto      — hidden exhaustion behind "I'm fine" language
 *   culpa_no_verbalizada    — unspoken guilt signal
 *   ninguno                 — no silent tension
 */

const TENSION_SIGNALS = [
  {
    type: "evasion",
    patterns: [
      /(?:no\s+(?:sé\s+cómo|quiero)\s+(?:hablar|mencionar))\s+(?:de\s+esto|del\s+tema)/i,
      /mejor\s+(?:lo\s+dejamos|no\s+entramos)\s+(?:así|en\s+eso)/i,
      /(?:en\s+realidad|la\s+verdad)\s+no\s+(?:es\s+(?:tan\s+)?para\s+tanto|importa\s+tanto)/i,
      /(?:prefiero|preferiría)\s+no\s+(?:profundizar|hablar\s+más)\s+(?:en|de)\s+eso/i,
    ],
  },
  {
    type: "agotamiento_oculto",
    patterns: [
      // "I'm fine" followed by exhaustion indicators in the same message
      /(?:estoy\s+bien|todo\s+(?:bien|está\s+bien))\s*[,;.]\s*(?:pero|aunque|solo\s+que)\s*(?:es\s+que\s+)?(?:a\s+veces|hay\s+momentos?)/i,
      /todo\s+(?:va\s+)?bien\s+(?:pero|aunque)\s+(?:siento\s+que\s+)?ya\s+no\s+(?:tengo|doy)\s+(?:fuerzas?|más)/i,
      /(?:me\s+esfuerzo|trato\s+de\s+seguir)\s+(?:pero|aunque)\s+(?:honestamente|la\s+verdad)\s+(?:estoy|me\s+siento)\s+(?:cansado|agotado)/i,
    ],
  },
  {
    type: "culpa_no_verbalizada",
    patterns: [
      // Minimizing or deflecting guilt
      /(?:no\s+sé\s+si\s+hice\s+(?:bien|lo\s+correcto)|quizás\s+debería\s+(?:haber|haberlo))/i,
      /(?:me\s+pregunto\s+si|a\s+veces\s+pienso\s+que)\s+(?:fallé|pude\s+haber\s+hecho\s+(?:más|mejor))/i,
      /(?:no\s+lo\s+digo\s+(?:abiertamente|directamente)|no\s+me\s+gusta\s+admitirlo)\s+pero\s+(?:siento|creo)\s+que\s+(?:fallé|debo)/i,
    ],
  },
  {
    type: "tension_espiritual",
    patterns: [
      /(?:todo\s+(?:está\s+)?bien|estoy\s+bien)\s+(?:espiritualmente|en\s+mi\s+fe)\s*[,;.]\s*(?:pero|aunque)/i,
      /(?:sigo\s+(?:orando|sirviendo)|no\s+he\s+(?:dejado|parado))\s+(?:pero|aunque)\s+(?:honestamente|la\s+verdad\s+es\s+que)/i,
      /(?:Dios\s+(?:sabe|entiende)|tengo\s+fe)\s+pero\s+(?:a\s+veces|hay\s+momentos)\s+(?:no\s+(?:siento|entiendo)|me\s+pregunto)/i,
    ],
  },
  {
    type: "conflicto_indirecto",
    patterns: [
      // Hinting at conflict without naming it
      /(?:las\s+cosas|la\s+situación|el\s+ambiente)\s+(?:están?\s+)?(?:un\s+poco|bastante)\s+(?:tensas?|complicadas?|difíciles?)\s+(?:últimamente|por\s+ahora)/i,
      /(?:hay\s+cosas\s+que|algunas\s+cosas)\s+(?:no\s+(?:se\s+dicen|se\s+hablan?)|están?\s+(?:pendientes?|sin\s+resolver))\s+(?:entre|con)/i,
      /(?:no\s+es\s+que\s+haya\s+conflicto|no\s+quiero\s+decir\s+que\s+hay\s+problema)\s+pero/i,
    ],
  },
];

/**
 * @param {{
 *   text:    string,
 *   emotion: object|null,
 *   intent:  string,
 * }} params
 * @returns {{
 *   hasTension:  boolean,
 *   type:        string,
 *   instruction: string|null,
 *   count:       number,
 * }}
 */
export function detectSilentTension({ text, emotion, intent }) {
  const t = text ?? "";

  const found = [];
  for (const { type, patterns } of TENSION_SIGNALS) {
    if (patterns.some((p) => p.test(t))) {
      found.push(type);
    }
  }

  if (found.length === 0) {
    return { hasTension: false, type: "ninguno", instruction: null, count: 0 };
  }

  const primaryType = found[0];

  return {
    hasTension:  true,
    type:        primaryType,
    instruction: _instruction(primaryType),
    count:       found.length,
  };
}

function _instruction(type) {
  if (type === "evasion") {
    return "Hay evasión. No insistas en lo que se evita — crea espacio con preguntas suaves sin presionar.";
  }
  if (type === "agotamiento_oculto") {
    return "Hay agotamiento oculto detrás de 'estoy bien'. Crea espacio para lo que no se dice completamente.";
  }
  if (type === "culpa_no_verbalizada") {
    return "Hay culpa no verbalizada. No la nombres directamente — permite que emerja a su ritmo.";
  }
  if (type === "tension_espiritual") {
    return "Hay tensión espiritual implícita. No la fuerzas — escucha lo que está debajo de las palabras correctas.";
  }
  if (type === "conflicto_indirecto") {
    return "Hay conflicto insinuado pero no nombrado. No lo precipites — haz preguntas abiertas que le den espacio.";
  }
  return null;
}
